import { useCallback } from "react";
import * as Sentry from "@sentry/browser";
import { useToken } from "@bn/api";
import { USE_INTERNAL_AUTH } from "@helpers";

import { useMandatorLinks } from "./useMandatorLinks";

const PORTAL_LOGOUT_TIMEOUT_MS = 3000;

// "no-cors": the portal's CORS answer may not allow credentials (ariva prod
// sends `*`), and only the side effect matters, not the response.
const endPortalSession = async (url: string) => {
  try {
    await Promise.race([
      fetch(url, {
        method: "POST",
        mode: "no-cors",
        credentials: "include",
        keepalive: true,
      }),
      new Promise((resolve) => setTimeout(resolve, PORTAL_LOGOUT_TIMEOUT_MS)),
    ]);
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const useLogout = () => {
  const { logout: tokenLogout } = useToken({ enabled: false });
  const { logoutRedirectUrl, logoutApiUrl } = useMandatorLinks();

  const logout = useCallback(async () => {
    tokenLogout();

    if (USE_INTERNAL_AUTH) {
      window.location.href = import.meta.env.VITE_APP_ROOT_URL!;
      return;
    }

    if (logoutApiUrl) {
      await endPortalSession(logoutApiUrl);
    }
    window.location.href =
      logoutRedirectUrl ?? import.meta.env.VITE_APP_ROOT_URL!;
  }, [tokenLogout, logoutRedirectUrl, logoutApiUrl]);

  return {
    logout,
  };
};
